import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import styled from 'styled-components';
import {Transition} from 'react-transition-group';
import dot from 'dot-object';

import Button from 'components/Button';
import {mq} from 'utils/responsive';

const StepsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100vw;
  transition: transform 0.3s ease;
  transform: translateX(${({activeIndex}) => -100 * activeIndex}vw);

  ${mq.ltsm`
    transition: none;
  `}
`;

const StepContainer = styled.div`
  flex: 0 0 100%;

  & > div {
    display: ${({state}) => (state === 'exited' ? 'none' : 'block')};
    position: ${({state}) => (state === 'exiting' || state === 'exited' ? 'absolute' : 'static')};
    width: 100%;
  }
`;

const Actions = styled.div`
  position: relative;
  grid-area: actions;
  display: flex;
  width: calc(100% - 80px);
  align-items: center;
  justify-content: space-between;
  margin: 32px 0 0;
  padding: 4px 40px 40px;
  z-index: 10;

  ${mq.ltsm`
    flex-direction: column-reverse;

    & button {
      margin: 0 0 12px;
    }
  `}
`;

const StepForm = ({
  steps,
  defaultValues,
  submitLabel,
  loadingLabel,
  onSubmit,
  submitting,
}) => {
  const [step, setStep] = useState(0);
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues,
  });
  const {
    handleSubmit,
    trigger,
    formState: {isDirty, errors},
    watch,
  } = formMethods;
  const formData = watch();
  const pageIsValid = useMemo(() => {
    if (steps[step].fields) {
      let fieldList = steps[step].fields;
      if (typeof steps[step].fields === 'function') {
        fieldList = steps[step].fields(formData);
      }

      return !fieldList.some((key) => dot.pick(key, errors));
    }

    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, step, errors]);
  const onPrevious = useCallback((evt) => {
    evt.preventDefault();
    if (step > 0) {
      setStep(step - 1);
    }
  }, [step]);
  const onNext = useCallback(async (evt) => {
    if (!pageIsValid) { return; }

    evt.preventDefault();

    setStep(step + 1);
    if (step < steps.length - 1) {
      if (steps[step + 1].fields) {
        let fieldList = steps[step + 1].fields;
        if (typeof steps[step + 1].fields === 'function') {
          fieldList = steps[step + 1].fields(formData);
        }

        await trigger(fieldList);
      }
    }
  }, [pageIsValid, step, steps, trigger, formData]);
  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StepsWrapper activeIndex={step}>
          {steps.map((Step, idx) => (
            <Transition key={`step${idx}`} in={step === idx} timeout={300}>
              {(state) => (
                <StepContainer state={state} active={step === idx}>
                  <div>
                    <Step />
                  </div>
                </StepContainer>
              )}
            </Transition>
          ))}
        </StepsWrapper>
        <Actions>
          {step > 0
            ? (
              <Button
                type="button"
                onClick={onPrevious}
                color="secondary"
                disabled={submitting}
              >
                &larr; Back
              </Button>
            )
            : (
              <div />
            )}
          {step < steps.length - 1
            ? (
              <Button type="button" onClick={onNext} disabled={!isDirty || !pageIsValid}>
                Continue &rarr;
              </Button>
            )
            : (
              <Button type="submit" disabled={!isDirty || !pageIsValid || submitting}>
                {submitting ? loadingLabel : submitLabel}
              </Button>
            )}
        </Actions>
      </form>
    </FormProvider>
  );
};

export default StepForm;
